/**
 * 구독 관리 페이지
 * FSD: pages/subscription-manage
 */

import React, { useState } from 'react';
import {
  useSubscription,
  useBillingHistory,
  usePauseSubscription,
  useResumeSubscription,
  useCancelSubscription,
  SubscriptionCard,
  BillingHistoryList,
  PauseModal,
  CancelModal,
  PaymentFailureAlert,
} from '@/features/subscription/subscription-manage';

const SubscriptionManagePage: React.FC = () => {
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const { data: subscription, isLoading, error } = useSubscription();
  const { data: billingHistory } = useBillingHistory(subscription?.id);

  const pauseMutation = usePauseSubscription();
  const resumeMutation = useResumeSubscription();
  const cancelMutation = useCancelSubscription();

  if (isLoading) {
    return (
      <div className="subscription-manage-page">
        <div className="loading-state">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-manage-page">
        <div className="error-state">오류: {(error as Error).message}</div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="subscription-manage-page">
        <div className="empty-state">활성 구독이 없습니다.</div>
      </div>
    );
  }

  const handlePause = async (reason?: string) => {
    try {
      await pauseMutation.mutateAsync({ id: subscription.id, reason });
      setShowPauseModal(false);
      alert('구독이 일시중지되었습니다');
    } catch (err: any) {
      alert(`일시중지 실패: ${err.message}`);
    }
  };

  const handleResume = async () => {
    try {
      await resumeMutation.mutateAsync(subscription.id);
      alert('구독이 재개되었습니다');
    } catch (err: any) {
      alert(`재개 실패: ${err.message}`);
    }
  };

  const handleCancel = async (reason?: string) => {
    try {
      await cancelMutation.mutateAsync({ id: subscription.id, reason });
      setShowCancelModal(false);
      alert('구독이 취소되었습니다');
    } catch (err: any) {
      alert(`취소 실패: ${err.message}`);
    }
  };

  return (
    <div className="subscription-manage-page">
      <div className="page-header">
        <h1>구독 관리</h1>
        <p className="subtitle">현재 구독 상태를 확인하고 관리할 수 있습니다</p>
      </div>

      <div className="page-content">
        <SubscriptionCard subscription={subscription} />

        {subscription.status === 'PAUSED' &&
          subscription.last_payment_failed_date && (
            <PaymentFailureAlert
              subscription={subscription}
              onRetry={handleResume}
            />
          )}

        <div className="subscription-actions">
          {subscription.status === 'ACTIVE' && (
            <>
              <button
                onClick={() => setShowPauseModal(true)}
                className="action-button pause-button"
              >
                구독 일시중지
              </button>
              <button
                onClick={() => setShowCancelModal(true)}
                className="action-button cancel-button"
              >
                구독 취소
              </button>
            </>
          )}

          {subscription.status === 'PAUSED' && (
            <>
              <button
                onClick={handleResume}
                className="action-button resume-button"
              >
                구독 재개
              </button>
              <button
                onClick={() => setShowCancelModal(true)}
                className="action-button cancel-button"
              >
                구독 취소
              </button>
            </>
          )}

          {subscription.status === 'CANCELLED' && (
            <div className="cancelled-notice">
              <p>이 구독은 취소되었습니다.</p>
            </div>
          )}
        </div>

        {billingHistory && billingHistory.billing_history.length > 0 && (
          <div className="billing-history-section">
            <h2>결제 내역</h2>
            {subscription.next_billing_date && (
              <p className="next-billing-date">
                다음 결제 예정일: <strong>{new Date(subscription.next_billing_date).toLocaleDateString()}</strong>
              </p>
            )}
            <BillingHistoryList history={billingHistory.billing_history} />
          </div>
        )}
      </div>

      {showPauseModal && (
        <PauseModal
          onPause={handlePause}
          onClose={() => setShowPauseModal(false)}
        />
      )}

      {showCancelModal && (
        <CancelModal
          onCancel={handleCancel}
          onClose={() => setShowCancelModal(false)}
        />
      )}
    </div>
  );
};

export default SubscriptionManagePage;
